import { useCallback, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { TbBarbell, TbCheck, TbDeviceMobile, TbDoorEnter } from 'react-icons/tb';
import { useAuth } from '../auth/AuthContext';
import { errorMessageFor } from '../auth/error-message';
import { PageState } from '../components/PageState';
import { StatusBadge } from '../components/StatusBadge';
import { ProfilePhotoPromo } from '../community/ProfilePhotoPromo';
import { InstallPrompt } from '../pwa/InstallPrompt';
import { formatDate, formatMoney } from '../lib/format';
import { memberApi as defaultMemberApi } from './member-api';
import { memberSettingsApi as defaultMemberSettingsApi } from './settings-api';
import { createTrainingApi } from '../training/training-api';
import { memberAttendanceApi as defaultAttendanceApi } from './attendance-api';
import './member.css';
import './my-routine.css';

const DAY_LABELS = {
  monday: 'Lunes', tuesday: 'Martes', wednesday: 'Miércoles', thursday: 'Jueves',
  friday: 'Viernes', saturday: 'Sábado', sunday: 'Domingo',
};

const defaultProgressApi = createTrainingApi();

function safeHttpUrl(value) {
  if (typeof value !== 'string' || !value.trim()) return null;
  try {
    const url = new URL(value.trim());
    return ['http:', 'https:'].includes(url.protocol) && url.hostname ? url.href : null;
  } catch {
    return null;
  }
}

function visibleScheduleDays(schedule) {
  if (!Array.isArray(schedule)) return [];
  return schedule.filter(item => item && item.visible === true && DAY_LABELS[item.day]);
}

/**
 * `/mi-cuenta` (spec "Alumno"): summarizes the signed-in member's own name,
 * goal, weekly target and the ONE period the server chose as relevant via
 * `relevantMembershipId` (Task 3's `selectRelevantMembershipId`, shared with
 * the admin dashboard). This looks up the membership whose `id` matches that
 * value — never the first/last element of `memberships`, and never a
 * recomputation from the browser's clock — so it always agrees with what
 * reception itself would consider "the" current period for this member.
 */
export function MemberHomePage({
  api = defaultMemberApi,
  settingsApi = defaultMemberSettingsApi,
  attendanceApi = defaultAttendanceApi,
  progressApi = defaultProgressApi,
}) {
  const { user, handleApiError } = useAuth();
  const userId = user && user.id;
  const [state, setState] = useState({ status: 'loading', data: null, error: '' });
  const [settings, setSettings] = useState(null);
  const [attendance, setAttendance] = useState({ status: 'loading', checkins: [], error: '' });
  const [attendanceDays, setAttendanceDays] = useState(null);
  const [progressError, setProgressError] = useState('');
  const [checkingIn, setCheckingIn] = useState(false);
  const [checkinFeedback, setCheckinFeedback] = useState({ kind: '', message: '' });

  const load = useCallback(async () => {
    if (!user) return;
    setState({ status: 'loading', data: null, error: '' });
    try {
      const data = await api.getOwnMember(user.id);
      setState({ status: 'loaded', data, error: '' });
    } catch (caught) {
      const outcome = handleApiError(caught, {
        clearBusinessData: () => setState({ status: 'loading', data: null, error: '' }),
      });
      if (outcome.handled) return;
      setState({ status: 'error', data: null, error: errorMessageFor(caught) });
    }
    // `user` is the whole session object; only its id decides what to fetch.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [api, user && user.id, handleApiError]);

  useEffect(() => {
    load();
  }, [load]);

  useEffect(() => {
    if (!userId) return undefined;
    let active = true;
    settingsApi.getSettings().then(result => {
      if (active) setSettings(result && result.settings ? result.settings : result);
    }).catch(() => {
      // Settings are supplementary; their failure must not hide the member's
      // own membership or routine shortcut.
      if (active) setSettings(null);
    });
    return () => { active = false; };
  }, [settingsApi, userId]);

  const loadAttendance = useCallback(async () => {
    try {
      const checkins = await attendanceApi.list({ limit: 8 });
      setAttendance({ status: 'loaded', checkins: Array.isArray(checkins) ? checkins : [], error: '' });
    } catch (caught) {
      const outcome = handleApiError(caught);
      if (outcome.handled) return;
      setAttendance({ status: 'error', checkins: [], error: errorMessageFor(caught) });
    }
  }, [attendanceApi, handleApiError]);

  const loadAttendanceDays = useCallback(async () => {
    try {
      const progress = await progressApi.getMyProgress();
      const lastWeek = Array.isArray(progress && progress.weekly) ? progress.weekly.at(-1) : null;
      setAttendanceDays(lastWeek && Number.isFinite(lastWeek.attendanceDays) ? lastWeek.attendanceDays : 0);
      setProgressError('');
    } catch (caught) {
      const outcome = handleApiError(caught);
      if (outcome.handled) return;
      setProgressError(errorMessageFor(caught));
    }
  }, [progressApi, handleApiError]);

  useEffect(() => {
    if (!userId) return undefined;
    loadAttendance();
    loadAttendanceDays();
  }, [userId, loadAttendance, loadAttendanceDays]);

  const checkIn = async () => {
    if (checkingIn) return;
    setCheckingIn(true);
    setCheckinFeedback({ kind: '', message: '' });
    try {
      const result = await attendanceApi.checkIn();
      setCheckinFeedback({
        kind: 'success',
        message: result && result.alreadyCheckedIn ? 'Ya figurás presente por hoy.' : 'Asistencia registrada. ¡Buen entrenamiento!',
      });
      await Promise.all([loadAttendance(), loadAttendanceDays()]);
    } catch (caught) {
      const outcome = handleApiError(caught);
      if (outcome.handled) return;
      setCheckinFeedback({ kind: 'error', message: errorMessageFor(caught) });
    } finally {
      setCheckingIn(false);
    }
  };

  // Defensive: in the real router `RoleRoute` unmounts this page the instant
  // the session clears (a 401 above), so `user` is never null while this
  // renders. Guarded anyway so this component never assumes that wiring.
  if (!user) return null;

  if (state.status === 'loading') {
    return <PageState title="Inicio" status="loading" />;
  }

  if (state.status === 'error') {
    return (
      <PageState
        title="Inicio"
        status="error"
        message={state.error}
        action={<button type="button" className="gym-button gym-button--secondary" onClick={load}>Reintentar</button>}
      />
    );
  }

  const { user: memberUser, profile, memberships, relevantMembershipId } = state.data;
  const relevant = (memberships || []).find(membership => membership.id === relevantMembershipId) || null;
  const displayName = (memberUser && memberUser.username) || user.username;
  const cuotaTitle = relevant && relevant.status === 'upcoming' ? 'Próxima cuota' : 'Cuota actual';

  return (
    <section className="gym-page" aria-labelledby="gym-page-title">
      <header className="gym-page__header">
        <h1 id="gym-page-title" className="gym-sr-only">Inicio</h1>
      </header>

      <div className="gym-member-grid">
        <ProfilePhotoPromo />

        <article className="gym-member-greeting">
          <h2 className="gym-member-greeting__title">Hola, {displayName}</h2>
          <dl className="gym-member-greeting__stats">
            <div className="gym-summary__row">
              <dt>Objetivo</dt>
              <dd>{(profile && profile.fitnessGoal) || 'Sin definir'}</dd>
            </div>
            <div className="gym-summary__row">
              <dt>Días por semana</dt>
              <dd>{(profile && profile.targetDaysPerWeek) || 'Sin definir'}</dd>
            </div>
          </dl>
        </article>

        <article className="gym-card gym-member-attendance gym-card--featured" aria-labelledby="gym-attendance-title">
          <div className="gym-member-attendance__heading">
            <div>
              <h2 id="gym-attendance-title" className="gym-card__title">Tus visitas</h2>
              <p className="gym-member-attendance__caption">Esta semana</p>
            </div>
            <p className="gym-member-attendance__count" aria-label={`${attendanceDays === null ? '—' : attendanceDays} visitas esta semana`}>
              {attendanceDays === null ? '—' : attendanceDays}
            </p>
          </div>
          {progressError ? <div className="gym-member-attendance__inline-error" role="alert">
            <span>No pudimos cargar las visitas de esta semana.</span>
            <button type="button" className="gym-member-attendance__retry" onClick={loadAttendanceDays}>Reintentar</button>
          </div> : null}
          <button type="button" className="gym-member-attendance__action" onClick={checkIn} disabled={checkingIn}>
            {!checkingIn && <TbCheck aria-hidden="true" focusable="false" />}
            {checkingIn ? 'Registrando…' : 'Marcar presente'}
          </button>
        </article>

        {checkinFeedback.message && <p className={`gym-member-attendance-feedback gym-member-attendance-feedback--${checkinFeedback.kind}`} role={checkinFeedback.kind === 'error' ? 'alert' : 'status'}>{checkinFeedback.message}</p>}

        <article className="gym-card gym-member-attendance gym-member-attendance-history" aria-labelledby="gym-attendance-history-title">
          <h3 id="gym-attendance-history-title" className="gym-member-attendance__list-title">Últimas asistencias</h3>
          {attendance.status === 'error' ? <div className="gym-member-attendance__inline-error" role="alert">
            <span>No pudimos cargar tus últimas asistencias.</span>
            <button type="button" className="gym-member-attendance__retry" onClick={loadAttendance}>Reintentar</button>
          </div> : <>
            {attendance.status === 'loading' ? <p className="gym-member-attendance__empty" role="status">Cargando asistencias…</p> : attendance.checkins.length ? (
              <ul className="gym-member-attendance__list" aria-label="Últimas asistencias">
                {attendance.checkins.map((checkin, index) => <li key={`${checkin.date}-${index}`}>
                  <span className="gym-member-attendance__origin" aria-hidden="true">
                    {checkin.source === 'kiosk' ? <TbDoorEnter /> : <TbDeviceMobile />}
                  </span>
                  <span className="gym-member-attendance__details">
                    <time dateTime={checkin.date}>{formatDate(checkin.date)}</time>
                    <span>{checkin.source === 'kiosk' ? 'Ingreso por recepción' : 'Marcada desde la app'}</span>
                  </span>
                </li>)}
              </ul>
            ) : <p className="gym-member-attendance__empty">Todavía no hay asistencias registradas.</p>}
          </>}
        </article>

        <article className="gym-card gym-member-quota">
          <h2 className="gym-card__title">{cuotaTitle}</h2>
          {relevant ? (
            <>
              <StatusBadge status={relevant.status} />
              <dl className="gym-summary">
                <div className="gym-summary__row">
                  <dt>Plan</dt>
                  <dd>{relevant.planName}</dd>
                </div>
                <div className="gym-summary__row">
                  <dt>Período</dt>
                  <dd>{formatDate(relevant.startDate)} – {formatDate(relevant.endDate)}</dd>
                </div>
                <div className="gym-summary__row">
                  <dt>Importe</dt>
                  <dd>{formatMoney(relevant.amount)}</dd>
                </div>
                <div className="gym-summary__row">
                  <dt>Pagado</dt>
                  <dd>{formatMoney(relevant.paid)}</dd>
                </div>
                <div className="gym-summary__row">
                  <dt>Saldo</dt>
                  <dd>{formatMoney(relevant.balance)}</dd>
                </div>
              </dl>
            </>
          ) : (
            <p className="gym-state__text">Todavía no tenés una cuota asignada</p>
          )}
        </article>

        <article className="gym-card gym-home-shortcut">
          <TbBarbell className="gym-home-shortcut__icon" aria-hidden="true" focusable="false" />
          <h2 className="gym-card__title gym-home-shortcut__title">Tu rutina</h2>
          <p className="gym-home-shortcut__text">Los ejercicios de cada día, con la demostración en video.</p>
          <Link to="/mi-cuenta/rutina" className="gym-home-shortcut__link">Ver mi rutina</Link>
        </article>

        <InstallPrompt variant="card" />

        {settings && <>
          {typeof settings.welcomeMessage === 'string' && settings.welcomeMessage.trim() && <article className="gym-card gym-member-settings gym-member-settings--welcome">
            <h2 className="gym-card__title">Mensaje de bienvenida</h2>
            <p className="gym-member-settings__welcome">{settings.welcomeMessage}</p>
          </article>}
          {(settings.address || settings.phone || (Array.isArray(settings.socialLinks) && settings.socialLinks.some(link => link && safeHttpUrl(link.url)))) && <article className="gym-card gym-member-settings">
            <h2 className="gym-card__title">Contacto</h2>
            <dl className="gym-summary">
              {settings.address && <div className="gym-summary__row"><dt>Dirección</dt><dd>{settings.address}</dd></div>}
              {settings.phone && <div className="gym-summary__row"><dt>Teléfono</dt><dd>{settings.phone}</dd></div>}
            </dl>
            {Array.isArray(settings.socialLinks) && settings.socialLinks.some(link => link && safeHttpUrl(link.url)) && <ul className="gym-member-settings__links">
              {settings.socialLinks.map((link, index) => {
                const href = link && safeHttpUrl(link.url);
                return href ? <li key={`${link.label || 'red'}-${index}`}><a href={href} target="_blank" rel="noreferrer">{link.label || href}</a></li> : null;
              })}
            </ul>}
          </article>}
          {visibleScheduleDays(settings.studentSchedule).length > 0 && <article className="gym-card gym-member-settings" aria-label="Horarios para alumnos">
            <h2 className="gym-card__title">Horarios</h2>
            <dl className="gym-summary">
              {visibleScheduleDays(settings.studentSchedule).map(item => <div className="gym-summary__row" key={item.day}>
                <dt>{DAY_LABELS[item.day]}</dt>
                <dd>{item.startTime && item.endTime ? `${item.startTime} – ${item.endTime}` : null}</dd>
              </div>)}
            </dl>
          </article>}
        </>}
      </div>
    </section>
  );
}
